import { useState } from "react";
import { motion } from "framer-motion";
import { useForm } from "react-hook-form";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { Lock, Eye, EyeOff, ArrowRight, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import AuroraBackground from "../components/AuroraBackground";
import Logo from "../components/Logo";
import { api, formatApiErrorDetail } from "../lib/api";

const EASE = [0.16, 1, 0.3, 1];

const inputCls =
  "w-full bg-white/5 border border-white/10 rounded-xl pl-11 pr-12 py-3.5 text-sm text-white placeholder-white/30 outline-none transition-[border-color,box-shadow] duration-300 focus:border-[#0066FF] focus:ring-2 focus:ring-[#0066FF]/40";

function ResetPasswordInput({ id, testId, placeholder, registration, error }) {
  const [show, setShow] = useState(false);
  return (
    <div>
      <div className="relative">
        <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" strokeWidth={1.5} />
        <input
          id={id}
          data-testid={testId}
          type={show ? "text" : "password"}
          placeholder={placeholder}
          className={inputCls}
          {...registration}
        />
        <button
          type="button"
          onClick={() => setShow((s) => !s)}
          className="absolute right-4 top-1/2 -translate-y-1/2 text-white/30 hover:text-white/70 transition-colors duration-200"
          aria-label={show ? "Hide password" : "Show password"}
        >
          {show ? <EyeOff className="w-4 h-4" strokeWidth={1.5} /> : <Eye className="w-4 h-4" strokeWidth={1.5} />}
        </button>
      </div>
      {error && <p className="text-xs text-red-400 mt-1.5">{error.message}</p>}
    </div>
  );
}

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [serverError, setServerError] = useState("");
  const [done, setDone] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm({ mode: "onBlur" });

  const onSubmit = async (values) => {
    setLoading(true);
    setServerError("");
    try {
      await api.post("/auth/reset-password", { token, new_password: values.password });
      setDone(true);
      toast.success("Password updated.");
    } catch (e) {
      setServerError(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="reset-password-page" className="min-h-screen bg-[#050814] text-white relative flex items-center justify-center px-5">
      <AuroraBackground />
      <motion.div
        initial={{ opacity: 0, y: 32 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.9, ease: EASE }}
        className="w-full max-w-md"
      >
        <div className="flex justify-center mb-10">
          <Logo />
        </div>
        <div className="glass rounded-3xl p-8 sm:p-10">
          {!token ? (
            <div className="text-center py-4" data-testid="reset-invalid-link">
              <p className="font-display text-2xl text-white tracking-tight">Invalid reset link</p>
              <p className="text-sm text-white/45 font-light mt-3 leading-relaxed">
                This link is missing its security token. Please request a fresh reset link.
              </p>
            </div>
          ) : done ? (
            <div className="text-center py-4" data-testid="reset-success-message">
              <div className="mx-auto w-14 h-14 rounded-2xl glass flex items-center justify-center mb-5">
                <ShieldCheck className="w-6 h-6 text-[#00F0FF]" strokeWidth={1.5} />
              </div>
              <p className="font-display text-2xl text-white tracking-tight">Password updated</p>
              <p className="text-sm text-white/45 font-light mt-3">
                You can now sign in with your new password.
              </p>
              <motion.button
                data-testid="reset-go-login-button"
                onClick={() => navigate("/login")}
                whileHover={{ scale: 1.01 }}
                whileTap={{ scale: 0.98 }}
                className="mt-8 w-full rounded-full py-3.5 text-sm font-semibold text-white bg-gradient-to-r from-[#0066FF] to-[#5E2BFF] shadow-[0_0_24px_rgba(0,102,255,0.35)] flex items-center justify-center gap-2"
              >
                Go to sign in
                <ArrowRight className="w-4 h-4" strokeWidth={2} />
              </motion.button>
            </div>
          ) : (
            <>
              <h2 className="font-display text-3xl tracking-[-0.02em] text-white" data-testid="reset-heading">
                Choose a new password
              </h2>
              <p className="text-sm text-white/45 font-light mt-2 mb-8">
                At least 8 characters. Make it strong.
              </p>
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-5" data-testid="reset-form">
                <ResetPasswordInput
                  id="reset-password"
                  testId="reset-password-input"
                  placeholder="New password"
                  registration={register("password", {
                    required: "Password is required.",
                    minLength: { value: 8, message: "Use at least 8 characters." },
                  })}
                  error={errors.password}
                />
                <ResetPasswordInput
                  id="reset-confirm"
                  testId="reset-confirm-input"
                  placeholder="Confirm new password"
                  registration={register("confirm", {
                    required: "Please confirm your password.",
                    validate: (v) => v === watch("password") || "Passwords do not match.",
                  })}
                  error={errors.confirm}
                />
                {serverError && (
                  <motion.p
                    initial={{ opacity: 0, y: -6 }}
                    animate={{ opacity: 1, y: 0 }}
                    data-testid="reset-error-message"
                    className="text-sm text-red-400 bg-red-400/10 border border-red-400/20 rounded-xl px-4 py-3"
                  >
                    {serverError}
                  </motion.p>
                )}
                <motion.button
                  type="submit"
                  data-testid="reset-submit-button"
                  disabled={loading}
                  whileHover={{ scale: 1.01 }}
                  whileTap={{ scale: 0.98 }}
                  className="w-full rounded-full py-3.5 text-sm font-semibold text-white bg-gradient-to-r from-[#0066FF] to-[#5E2BFF] shadow-[0_0_24px_rgba(0,102,255,0.35)] hover:shadow-[0_0_40px_rgba(94,43,255,0.5)] transition-shadow duration-300 disabled:opacity-60 flex items-center justify-center gap-2"
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Update password"}
                </motion.button>
              </form>
            </>
          )}
        </div>
        <p className="text-center mt-6">
          <Link
            to="/login"
            data-testid="reset-back-login-link"
            className="text-xs text-white/40 hover:text-white transition-colors duration-200"
          >
            Back to sign in
          </Link>
        </p>
      </motion.div>
    </div>
  );
}
